import moment from 'moment';
import { Typography } from 'antd';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { ICS2ArrivalNotificationMessage } from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { getIcs2NotificationMessageLabel } from 'store/declarations/eu/ics2/ics2-notification';
import useICS2Codelists from 'hooks/useICS2Codelists';
import useIcs2FunctionalErrorLabel from 'hooks/useIcs2FunctionalErrorLabel';
import DetailsHeader from 'views/declarations/common/ics2-message-details/DetailsHeader';
import MessageDetails from 'views/declarations/common/ics2-message-details/MessageDetails';
import { asList, errorsOf, readDateTime, readField, readText } from '../arrivalMessageUtils';

const DATE_FORMAT = 'DD/MM/YYYY, HH:mm';

type Props = {
    message: ICS2ArrivalNotificationMessage;
    onBack: () => void;
};

/**
 * House consignments that could not be marked as arrived.
 *
 * The arrival itself is still registered - this message follows a successful registration and only
 * reports the consignments that were already presented or controlled.
 *
 * The errors are read by their CL723 code rather than their text: customs currently sends "ENS
 * filing in a wrong state" here where it means the house consignment, so the description cannot be
 * relied on to say what the message is about.
 */
const IE3N07Details = ({ message, onBack }: Props) => {
    const { getICS2CodeDescription } = useICS2Codelists();
    const getIcs2FunctionalErrorLabel = useIcs2FunctionalErrorLabel();

    const office = readText(message, ['customsOfficeOfFirstEntry', 'referenceNumber']);
    const notifiedAt = readDateTime(message, ['notificationDate']);
    const messageTypeLabel = getIcs2NotificationMessageLabel(message.messageType);

    const houseDocuments = asList(readField(message, ['transportDocumentHouse']));
    const errors = errorsOf(message);

    const metadata: Record<string, string> = {
        ...(message.messageType
            ? { 'Message Type': `${message.messageType}${messageTypeLabel ? ` - ${messageTypeLabel}` : ''}` }
            : {}),
        ...(readText(message, ['MRN']) ? { MRN: readText(message, ['MRN'])! } : {}),
        ...(notifiedAt ? { 'Issued At': moment(notifiedAt).format(DATE_FORMAT) } : {}),
        ...(office
            ? { 'Customs Office of First Entry': getICS2CodeDescription(office, ICS2CodelistsMap.CL141) || office }
            : {}),
        Received: moment(message.receivedAt).format(DATE_FORMAT),
    };

    return (
        <>
            <DetailsHeader onBack={onBack} />

            <MessageDetails
                messageMetadata={metadata}
                details={{
                    whatHappened: (
                        <>
                            <Typography.Paragraph>
                                The arrival is registered, but some of the house consignments it covers could not be
                                moved to the arrived state - customs already has them recorded as presented or
                                controlled.
                            </Typography.Paragraph>

                            {!!houseDocuments.length && (
                                <>
                                    <Typography.Text strong>Affected house transport documents</Typography.Text>
                                    <ul style={{ listStyleType: 'initial' }}>
                                        {houseDocuments.map((document, index) => (
                                            <li key={`${document.documentNumber ?? 'document'}-${index}`}>
                                                {String(document.documentNumber ?? '-')}
                                                {document.type ? ` (${String(document.type)})` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </>
                    ),
                    whyItHappened: errors.length ? (
                        <ul style={{ marginBottom: 0, listStyleType: 'initial' }}>
                            {errors.map((error, index) => (
                                <li key={`${error.validationCode ?? 'error'}-${index}`}>
                                    <Typography.Text strong>{error.validationCode ?? '-'}</Typography.Text>
                                    {' - '}
                                    {getIcs2FunctionalErrorLabel(error.validationCode) ??
                                        error.description ??
                                        'Unknown error'}
                                    {error.pointer?.messageElementPath ? ` (${error.pointer.messageElementPath})` : ''}
                                </li>
                            ))}
                        </ul>
                    ) : undefined,
                    nextSteps: (
                        <ul style={{ marginBottom: 0, listStyleType: 'initial' }}>
                            <li>Nothing has to be sent to customs - this notification expects no reply.</li>
                            <li>
                                Find out why the consignments are already presented or controlled. It usually points at
                                a step of the supply chain being recorded out of order, or at the wrong ENS being
                                referenced.
                            </li>
                            <li>
                                Involve the member state or the other parties in the chain if it cannot be explained.
                            </li>
                        </ul>
                    ),
                }}
            />
        </>
    );
};

export default IE3N07Details;
